import { useQuery, useQueryClient } from 'react-query';
import api from 'api';
import { FBP_STATUS_FILTERS } from 'components/SalaryComponents/views/FlexibleBenefits/constants';
import { ComponentModule } from 'api/gql-utils/bonusAssignment/types';
import {
  getMergedFlexibleBenefitsData,
  getPercentageValue,
  isFBPUnitAmount,
  updateEmployerNPSComponentBasedOnRegime,
} from '../utils';
import { useCallback, useRef, useReducer } from 'react';
import { useSelector } from 'react-redux';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import { fbpAssignmentsReducer } from '../reducers/fbpAssignmentsReducer';
import { queryKeys } from '../constants';
import { getFinancialYearFromDate } from 'utils/Dates';
import { useEmployeeSalaryDetails } from './useEmployeeSalaryDetails';
import useFeature from 'hooks/useFeature';

type UseAssignedComponentsArgs = {
  userId?: string | number;
  shouldGetUnassignedComponents?: boolean;
};

export const useAssignedComponents = ({
  userId,
  shouldGetUnassignedComponents = false,
}: UseAssignedComponentsArgs) => {
  const stringUserId = userId?.toString() ?? '';

  const [state, dispatch] = useReducer(fbpAssignmentsReducer, {
    monthlyDeclarations: {},
    monthlyDeclarationErrors: {},
    selectedIds: [],
    yearlyDeclarationAmountValues: {},
  });

  const { totalAnnualCTC, totalBase } = useEmployeeSalaryDetails();
  const { isFbpPercentagePrecisionEnabled } = useFeature('fbpPercentagePrecision');
  const queryClient = useQueryClient();

  const monthlyDeclarationSnapShot = useRef<Record<string, string>>({});
  const selectedIdSnapShot = useRef<string[]>([]);

  const onMonthlyDeclarationChange = useCallback(({ id, value }: { id: string; value: string }) => {
    dispatch({
      type: 'MONTHLY_DECLARATION_CHANGE',
      payload: { id, value },
    });

    updateMonthlyDeclarationValueInAmount({ id, value });
  }, []);

  const onSelectedIdsChange = useCallback((ids: string[]) => {
    dispatch({
      type: 'SET_SELECTED_IDS',
      payload: ids,
    });
  }, []);

  const orgId = useSelector(loggedInUserSelectors.organizationID);
  const merchantId = orgId ? `org_${orgId}` : '';
  const currentFinancialYear = getFinancialYearFromDate(new Date());

  const taxRegimeQuery = useQuery({
    queryKey: [queryKeys.FETCH_EMPLOYEE_TAX_REGIME, stringUserId, currentFinancialYear],
    queryFn: () =>
      api.nps.getEmployeeTaxRegime({
        userId: stringUserId,
        financialYear: currentFinancialYear,
        merchantId,
      }),
    select: (data) => data.data.tax_regime,
    enabled: Boolean(userId) && Boolean(merchantId),
  });

  const assignedQuery = useQuery({
    queryKey: ['assigned-components', stringUserId],
    queryFn: () =>
      api.flexibleBenefitComponents.queries.getAssignedFlexibleComponentsList({
        user_id: stringUserId,
        isFbpDecimalEnabled: isFbpPercentagePrecisionEnabled,
      }),
    enabled: Boolean(userId),
    select: (data) => {
      const assigned =
        data.payrollListAssignedComponents.data?.map((item) => ({
          ...item,
          id: item.assignment_id,
        })) ?? [];

      return {
        assignedFlexibleComponents: assigned,
      };
    },
  });

  const flexibleQuery = useQuery({
    queryKey: ['flexible-benefits', stringUserId],
    // React 18: automatic batching means flexibleQuery may fire before assignedQuery.data
    // is committed to component state. Using isSuccess guarantees data is available in closure.
    enabled: assignedQuery.isSuccess && !taxRegimeQuery.isLoading && !taxRegimeQuery.isError,
    queryFn: () =>
      api.flexibleBenefitComponents.queries.getFlexibleBenefitsList({
        status: FBP_STATUS_FILTERS.active,
        offset: 0,
        limit: 100,
        isFbpDecimalEnabled: isFbpPercentagePrecisionEnabled,
        type: ComponentModule.FBP,
      }),
    select: (data) => {
      const isNewRegime = taxRegimeQuery.data === 'new';

      const flexibleComponents = updateEmployerNPSComponentBasedOnRegime(
        data.payrollListFlexibleBenefitsComponents.data,
        isNewRegime,
      );

      const flexibleComponentsData = getMergedFlexibleBenefitsData({
        flexibleComponents,
        assignedFlexibleComponents: assignedQuery.data?.assignedFlexibleComponents,
        unassignedComponents: shouldGetUnassignedComponents,
      });

      const monthlyDeclarationsInAmount: Record<string, string> = {};

      const totals = flexibleComponentsData.reduce(
        (acc, curr) => {
          const isAmount = isFBPUnitAmount(curr?.unit);
          const baseValue =
            curr?.unit === 'PERCENTAGE_OF_CTC' || curr?.unit === 'PERCENTAGE_OF_GROSS'
              ? totalAnnualCTC
              : curr?.unit === 'PERCENTAGE_OF_BASIC'
              ? totalBase
              : '0';
          const declaredValue = isAmount
            ? Number(curr.monthlyDeclaration.value) * 12
            : getPercentageValue(Number(baseValue), Number(curr.monthlyDeclaration.value));

          monthlyDeclarationsInAmount[curr.id] = declaredValue?.toString();

          const allocatedValue =  isAmount
          ? Number(curr.maxValuePerMonth) * 12
          : getPercentageValue(Number(baseValue), Number(curr.maxValuePerMonth))

          acc.totalAllocated += Math.round(allocatedValue);
          acc.totalAssigned += declaredValue;

          return acc;
        },
        { totalAssigned: 0, totalAllocated: 0 },
      );

      const totalUnallocated = totals.totalAllocated - totals.totalAssigned;
      return {
        data: flexibleComponentsData,
        ...totals,
        totalUnallocated,
        monthlyDeclarationsInAmount,
      };
    },
    onSuccess: (data) => {
      const payload = data.data.map((node) => ({
        id: node.id,
        value: node.monthlyDeclaration.value,
        assignment_id: node.assignment_id,
      }));

      const monthlyDeclarationsSnapShotData = payload.reduce(
        (acc, curr) => ({ ...acc, [curr.id]: curr.value }),
        {},
      );

      if (Object.keys(monthlyDeclarationSnapShot.current).length === 0) {
        dispatch({
          type: 'SET_MONTHLY_DECLARATIONS',
          payload: payload,
        });
      }

      monthlyDeclarationSnapShot.current = monthlyDeclarationsSnapShotData;

      const selectedIdsData = payload.filter((item) => item.assignment_id).map((item) => item.id);
      if (selectedIdSnapShot.current.length === 0)
        dispatch({
          type: 'SET_SELECTED_IDS',
          payload: selectedIdsData,
        });
      selectedIdSnapShot.current = selectedIdsData;

      const monthlyDeclarationAmountValuesPayload = data.monthlyDeclarationsInAmount;
      dispatch({
        type: 'SET_YEARLY_DECLARATION_AMOUNT_VALUES',
        payload: monthlyDeclarationAmountValuesPayload,
      });
    },
  });

  const updateMonthlyDeclarationValueInAmount = ({ id, value }: { id: string; value: string }) => {
    const componentData = flexibleQuery.data?.data.find((component) => component.id === id);
    if (!componentData) return;
    const parsedValue = !!value ? value : '0';
    const isAmount = isFBPUnitAmount(componentData?.unit);
    const baseValue =
      componentData?.unit === 'PERCENTAGE_OF_CTC' || componentData?.unit === 'PERCENTAGE_OF_GROSS'
        ? totalAnnualCTC
        : componentData?.unit === 'PERCENTAGE_OF_BASIC'
        ? totalBase
        : '0';
    const valueInAmount = isAmount
      ? Number(parsedValue) * 12
      : getPercentageValue(Number(baseValue), Number(parsedValue));

    dispatch({
      type: 'YEARLY_DECLARATION_IN_AMOUNT_CHANGE',
      payload: {
        id,
        value: valueInAmount?.toString(),
      },
    });
  };

  const invalidateQueries = async () => {
    await queryClient.invalidateQueries(['flexible-benefits', stringUserId]);

    await queryClient.invalidateQueries(['assigned-components', stringUserId]);

    await queryClient.refetchQueries({
      queryKey: ['assigned-components', stringUserId],
    });
  };

  const refetchData = async () => {
    if (assignedQuery.isError) await assignedQuery.refetch();
    flexibleQuery.refetch();
  };

  return {
    assignedFlexibleComponents: assignedQuery.data?.assignedFlexibleComponents,

    errorAssignedComponents: assignedQuery.error,

    flexibleComponentsData: flexibleQuery.data?.data,

    totalAssigned: flexibleQuery.data?.totalAssigned ?? 0,
    totalAllocated: flexibleQuery.data?.totalAllocated ?? 0,
    totalUnallocated: flexibleQuery.data?.totalUnallocated ?? 0,

    taxRegime: taxRegimeQuery.data,
    isTaxRegimeLoading: taxRegimeQuery.isLoading,

    onMonthlyDeclarationChange,
    userId,
    monthlyDeclarationSnapShot,
    selectedIdSnapShot,

    onSelectedIdsChange,
    invalidateQueries,

    setMonthlyDeclarationErrors: (errors: Record<string, string>) =>
      dispatch({ type: 'SET_ERRORS', payload: errors }),
    refetchData,

    monthlyDeclarations: state.monthlyDeclarations,
    yearlyDeclarationsInAmount: state.yearlyDeclarationAmountValues,
    monthlyDeclarationErrors: state.monthlyDeclarationErrors,
    // React 18: onSuccess dispatch may not be in the same batch as isSuccess becoming true.
    // Derive selectedIds from query data directly when the reducer hasn't been populated
    // yet (snapshot empty = user hasn't interacted, reducer state empty = onSuccess not yet fired).
    selectedIds:
      flexibleQuery.isSuccess && state.selectedIds.length === 0 && selectedIdSnapShot.current.length === 0
        ? (flexibleQuery.data?.data.filter((c) => !!c.assignment_id).map((c) => c.id) ?? [])
        : state.selectedIds,

    isFetching: assignedQuery.isFetching || flexibleQuery.isFetching,
    isLoading: assignedQuery.isLoading || flexibleQuery.isLoading,
    isError: assignedQuery.isError || flexibleQuery.isError,
    error: assignedQuery.error || flexibleQuery.error,
    isFlexibleDataReady: flexibleQuery.isSuccess,
  };
};
